import { Bell } from 'lucide-react'
import { useState } from 'react'
import { UsageAlertConfiguredState } from '@/components/shared/usage-alert-card/configured-state'
import { DeleteUsageAlertDialog } from '@/components/shared/usage-alert-card/delete-dialog'
import { UsageAlertEmptyState } from '@/components/shared/usage-alert-card/empty-state'
import { USAGE_ALERT_CONFIG_QUERY } from '@/components/shared/usage-alert-card/query'
import { UsageAlertSheet } from '@/components/shared/usage-alert-card/sheet'
import { H3 } from '@/components/sv/typographies'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useIdCompany, useRBAC, useSuspenseQuery } from '@/lib/hooks'
import type {
  UsageAlertConfigQuery,
  UsageAlertConfigQueryVariables,
} from '@/components/shared/usage-alert-card/query.types.gen'

type Props = {
  scopeLabel: string
  idProject?: string
  currentSpendUsd: number
  periodEnd: Date | null
}

export const UsageAlertCard = ({
  scopeLabel,
  idProject,
  currentSpendUsd,
  periodEnd,
}: Props) => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const [isSheetOpen, setSheetOpen] = useState(false)
  const [isDeleteOpen, setDeleteOpen] = useState(false)

  const canUpdate = rbac.company(idCompany).billing.update.can()
  const canViewUsage = idProject
    ? rbac.company(idCompany).project(idProject).usage.view.can()
    : rbac.company(idCompany).usage.view.can()

  const { data } = useSuspenseQuery<
    UsageAlertConfigQuery,
    UsageAlertConfigQueryVariables
  >(USAGE_ALERT_CONFIG_QUERY, {
    variables: { idCompany, idProject },
  })

  const config = data?.company?.usageAlertConfig ?? null

  if (idProject) {
    if (!config && !canUpdate) {
      return null
    }
    if (config && !canViewUsage) {
      return null
    }
  }

  const sheetConfig = config
    ? {
        id: config.id,
        limitUsd: config.limitUsd,
        emails: config.emails,
        triggers: config.triggers.map(({ percentage }) => ({ percentage })),
      }
    : undefined

  return (
    <>
      <Card className="bg-secondary p-0 gap-0">
        <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
          <CardTitle>
            <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
              <Bell className="size-4" /> Usage alert
            </H3>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Card className="p-0 border-x-0 border-b-0 shadow-none">
            <CardContent className="py-4 px-6">
              {config ? (
                <UsageAlertConfiguredState
                  scopeLabel={scopeLabel}
                  currentSpendUsd={currentSpendUsd}
                  limitUsd={config.limitUsd}
                  periodEnd={periodEnd}
                  triggers={config.triggers}
                  emails={config.emails}
                  canUpdate={canUpdate}
                  onUpdate={() => setSheetOpen(true)}
                  onDelete={() => setDeleteOpen(true)}
                />
              ) : (
                <UsageAlertEmptyState
                  canCreate={canUpdate}
                  onCreate={() => setSheetOpen(true)}
                />
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>

      {canUpdate && (
        <UsageAlertSheet
          isOpen={isSheetOpen}
          onClose={() => setSheetOpen(false)}
          idCompany={idCompany}
          idProject={idProject}
          scopeLabel={scopeLabel}
          config={sheetConfig}
        />
      )}

      {canUpdate && config && (
        <DeleteUsageAlertDialog
          isOpen={isDeleteOpen}
          onClose={() => setDeleteOpen(false)}
          idConfig={config.id}
        />
      )}
    </>
  )
}
